import React, { useEffect, useState } from "react";
import { Search, ArrowRight, SlidersHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

const ALL_SPECIALTIES = [
  "All", "Urban", "Contemporary", "Afrofusion", "Afro", "Bollywood",
  "Classical", "Breaking", "Hip-Hop", "Fusion", "Commercial", "Street", "Choreography",
];

const ArtistCard = ({ artist, onClick }) => (
  <div
    onClick={onClick}
    className="artist-card relative overflow-hidden rounded-sm cursor-pointer group"
    data-testid={`artist-card-${artist.id}`}
  >
    <div className="relative" style={{ paddingBottom: "130%" }}>
      <img
        src={artist.image || "/images/artist-1.jpg"}
        alt={artist.name}
        onError={(e) => { e.currentTarget.src = "/images/artist-1.jpg"; }}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
      <div className="card-overlay absolute inset-0 border border-gold/40" />
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <div className="flex flex-wrap gap-1 mb-2.5">
          {artist.specialties.slice(0, 2).map((spec) => (
            <span key={spec} className="specialty-tag" style={{ fontSize: "0.55rem" }}>{spec}</span>
          ))}
        </div>
        <h3 className="font-display text-xl font-light text-white mb-0.5">{artist.name}</h3>
        <p className="font-body text-white/40 text-[0.6rem] tracking-wider uppercase mb-3">{artist.title}</p>
        <div className="card-overlay flex items-center gap-1.5 text-gold text-[0.65rem] font-semibold tracking-widest uppercase">
          <span>View Profile</span>
          <ArrowRight size={11} />
        </div>
      </div>
    </div>
  </div>
);

const PortalPage = () => {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/artists")
      .then((r) => setArtists(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = artists.filter((a) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      a.name.toLowerCase().includes(q) ||
      a.title.toLowerCase().includes(q) ||
      a.specialties.some((s) => s.toLowerCase().includes(q));
    const matchFilter = activeFilter === "All" || a.specialties.includes(activeFilter);
    return matchSearch && matchFilter;
  });

  return (
    <div className="min-h-screen bg-[#121212] pt-24 pb-20" data-testid="portal-page">
      <div className="max-w-7xl mx-auto px-6 mb-14">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-8 bg-gold/60" />
          <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Private Client Portal</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="font-display font-light text-5xl md:text-6xl text-white leading-tight">Artist Roster</h1>
            <p className="font-body text-white/40 text-sm mt-3 tracking-wide">
              {filtered.length} {filtered.length === 1 ? "artist" : "artists"} available
            </p>
          </div>
          <div className="relative max-w-xs w-full">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/50" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search artists..."
              className="input-gold w-full pl-11 pr-4 py-3 rounded-sm text-sm font-body"
              data-testid="portal-search"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mb-10">
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-white/50 hover:text-gold transition-colors text-xs font-body tracking-widest uppercase"
            data-testid="portal-filter-toggle"
          >
            <SlidersHorizontal size={14} />
            <span>Filter</span>
          </button>
          <div className="h-px flex-1 bg-gold/10" />
          <span className="font-body text-white/20 text-xs">{filtered.length} results</span>
        </div>

        <div className={`flex flex-wrap gap-2 transition-all duration-300 overflow-hidden ${showFilters ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}>
          {ALL_SPECIALTIES.map((spec) => (
            <button
              key={spec}
              onClick={() => setActiveFilter(spec)}
              className={`specialty-tag cursor-pointer transition-all ${activeFilter === spec ? "bg-gold/20 border-gold text-gold" : ""}`}
              data-testid={`portal-filter-${spec}`}
            >
              {spec}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="w-10 h-10 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-32">
            <p className="font-display text-4xl text-white/20 mb-3">No artists found</p>
            <p className="font-body text-white/30 text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((a) => (
              <ArtistCard key={a.id} artist={a} onClick={() => navigate(`/artists/${a.id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalPage;
